import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithBackground.js";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;
const ContentWithPaddingXl = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24 px-4 sm:px-6 lg:px-8`;
const ContentWithVerticalPadding = tw.div`py-20 lg:py-24`;
const Content2Xl = tw.div`max-w-screen-2xl mx-auto`;

const Heading = tw.h1`font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-gray-900 leading-tight`;
const HeadingRow = tw.div`flex flex-col items-center text-center`;
const Text = tw.div`mt-8 text-base text-gray-600 max-w-3xl mx-auto`;

const ProcessSection = tw.div`mt-12`;
const ProcessGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8`;
const ProcessCard = tw.div`bg-white rounded-lg shadow-lg p-8 text-center`;
const ProcessNumber = tw.div`text-4xl font-bold text-primary-500 mb-4`;
const ProcessTitle = tw.h3`text-xl font-semibold text-gray-900 mb-2`;
const ProcessDescription = tw.p`text-gray-600`;

const ProgramSection = tw.div`mt-16`;
const ProgramGrid = tw.div`grid grid-cols-1 md:grid-cols-2 gap-8 mt-8`;
const ProgramCard = tw.div`bg-white rounded-lg shadow-lg p-8`;
const ProgramTitle = tw.h3`text-xl font-semibold text-gray-900 mb-4`;
const ProgramDescription = tw.p`text-gray-600 mb-4`;
const ProgramList = tw.ul`space-y-2`;
const ProgramItem = tw.li`flex items-center text-gray-600`;

const SupportSection = tw.div`mt-16`;
const SupportCard = tw.div`bg-white rounded-lg shadow-lg p-8`;
const SupportTitle = tw.h3`text-xl font-semibold text-gray-900 mb-4`;
const SupportDescription = tw.p`text-gray-600 mb-4`;
const SupportContact = tw.div`mt-6 p-4 bg-gray-100 rounded-lg`;
const SupportContactTitle = tw.h4`text-lg font-semibold text-gray-900 mb-2`;
const SupportContactInfo = tw.p`text-gray-600`;

export default () => {
  const process = [
    {
      number: "1",
      title: "Analyse & Planification",
      description: "Nous évaluons vos besoins en sécurité et en gestion des visiteurs pour adapter Tanga à votre organisation."
    },
    {
      number: "2",
      title: "Configuration & Formation",
      description: "Mise en place de la plateforme, personnalisation des accès, et formation de vos équipes à l'utilisation de Tanga."
    },
    {
      number: "3",
      title: "Déploiement Rapide",
      description: "Activation du système, importation des premiers visiteurs, et accompagnement lors des premiers check-ins."
    },
    {
      number: "4",
      title: "Suivi & Optimisation",
      description: "Support continu, analyse des rapports et ajustements pour garantir une expérience optimale et sécurisée."
    }
  ];

  const programs = [
    {
      title: "Accompagnement Sécurité & Digitalisation",
      description: "Un accompagnement personnalisé pour sécuriser et digitaliser votre accueil visiteurs.",
      features: [
        "Audit de sécurité initial",
        "Paramétrage sur mesure",
        "Formation des équipes sécurité et accueil",
        "Support technique dédié",
        "Suivi post-déploiement"
      ]
    },
    {
      title: "Support & Assistance",
      description: "Une équipe disponible pour répondre à toutes vos questions et garantir la continuité de service.",
      features: [
        "Assistance 24/7",
        "Documentation complète",
        "Mises à jour régulières",
        "Conseils d'optimisation",
        "Réunions de suivi"
      ]
    }
  ];

  return (
    <AnimationRevealPage>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>Onboarding & Intégration</Heading>
          </HeadingRow>
          <Text>
            <p className="text-center mb-8">
              L'intégration de Tanga dans votre organisation est simple, rapide et accompagnée à chaque étape. Sécurisez et digitalisez la gestion de vos visiteurs en toute confiance grâce à notre processus d'onboarding sur-mesure.
            </p>
          </Text>

          <ProcessSection>
            <HeadingRow>
              <Heading>Notre Processus</Heading>
            </HeadingRow>
            <Text>
              <p className="text-center mb-8">
                Un parcours structuré pour vous accompagner dans votre intégration.
              </p>
            </Text>

            <ProcessGrid>
              {process.map((step, index) => (
                <ProcessCard key={index}>
                  <ProcessNumber>{step.number}</ProcessNumber>
                  <ProcessTitle>{step.title}</ProcessTitle>
                  <ProcessDescription>{step.description}</ProcessDescription>
                </ProcessCard>
              ))}
            </ProcessGrid>
          </ProcessSection>

          <ProgramSection>
            <HeadingRow>
              <Heading>Nos Programmes</Heading>
            </HeadingRow>
            <Text>
              <p className="text-center mb-8">
                Des programmes conçus pour votre succès et votre développement.
              </p>
            </Text>

            <ProgramGrid>
              {programs.map((program, index) => (
                <ProgramCard key={index}>
                  <ProgramTitle>{program.title}</ProgramTitle>
                  <ProgramDescription>{program.description}</ProgramDescription>
                  <ProgramList>
                    {program.features.map((feature, featureIndex) => (
                      <ProgramItem key={featureIndex}>
                        <span className="mr-2">✓</span>
                        {feature}
                      </ProgramItem>
                    ))}
                  </ProgramList>
                </ProgramCard>
              ))}
            </ProgramGrid>
          </ProgramSection>

          <SupportSection>
            <HeadingRow>
              <Heading>Support & Contact</Heading>
            </HeadingRow>
            <Text>
              <p className="text-center mb-8">
                Notre équipe est là pour vous accompagner à chaque étape.
              </p>
            </Text>

            <SupportCard>
              <SupportTitle>Besoin d'aide ?</SupportTitle>
              <SupportDescription>
                Notre équipe d'intégration est disponible pour répondre à toutes vos questions
                et vous accompagner dans votre parcours chez Tanga.
              </SupportDescription>
              <SupportContact>
                <SupportContactTitle>Contactez-nous</SupportContactTitle>
                <SupportContactInfo>
                  Email : integration@tangagroup.africa<br />
                  Téléphone : +225 27 22 49 49 49
                </SupportContactInfo>
              </SupportContact>
            </SupportCard>
          </SupportSection>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
}; 